import { PartFinderBatch } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { useCancelPartFinderBatch } from '@/hooks/api/part-finder-batches/useCancelPartFinderBatch';
import { useToast } from '@/hooks/useToast';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useState } from 'react';

export interface CancelBatchModalProperties {
  batch: PartFinderBatch;
  open: boolean;
  onClose: () => void;
  onCancelled?: () => void;
}

export const CancelBatchModal = ({ batch, open, onClose, onCancelled }: CancelBatchModalProperties) => {
  const { showToast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { mutate, isPending } = useCancelPartFinderBatch({
    onSuccess: () => {
      showToast({ message: 'Batch cancelled', type: 'success' });
      onCancelled?.();
      onClose();
    },
    onError: setErrorMessage,
  });

  const handleClose = () => {
    if (isPending) return;
    setErrorMessage(undefined);
    onClose();
  };

  const handleConfirm = () => {
    setErrorMessage(undefined);
    mutate(batch.id);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Cancel batch</DialogTitle>
      <DialogContent>
        <ApiErrorAlert error={errorMessage ? { message: errorMessage } : undefined} />
        <DialogContentText>
          Cancel batch &ldquo;{getBatchLabel(batch)}&rdquo;? Pending parts won&apos;t run. In-flight reports finish on
          their own.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending} color='inherit'>
          Keep running
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isPending}
          variant='contained'
          color='warning'
          startIcon={isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          Cancel batch
        </Button>
      </DialogActions>
    </Dialog>
  );
};
